import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "../lib/supabase";

async function generateSlug(title) {
  const translitMap = {
    "а": "a", "б": "b", "в": "v", "г": "g", "д": "d", "е": "e", "ё": "yo",
    "ж": "zh", "з": "z", "и": "i", "й": "j", "к": "k", "л": "l", "м": "m",
    "н": "n", "о": "o", "п": "p", "р": "r", "с": "s", "т": "t", "у": "u",
    "ф": "f", "х": "kh", "ц": "ts", "ч": "ch", "ш": "sh", "щ": "shch",
    "ъ": "", "ы": "y", "ь": "", "э": "e", "ю": "yu", "я": "ya"
  };
  const transliterated = title
    .toLowerCase()
    .split("")
    .map((char) => translitMap[char] || char)
    .join("");
  const base = transliterated
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
  const slug = base.slice(0, 80);
  if (!slug) return "statya-" + Date.now().toString(36);

  const { data } = await supabase
    .from("articles")
    .select("slug")
    .like("slug", `${slug}%`);

  if (!data || data.length === 0) return slug;

  const existingSlugs = new Set(data.map((a) => a.slug));
  if (!existingSlugs.has(slug)) return slug;

  let counter = 2;
  while (existingSlugs.has(`${slug}-${counter}`)) {
    counter++;
  }
  return `${slug}-${counter}`;
}

function estimateReadingTime(text) {
  const words = text.replace(/<[^>]*>/g, "").split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 200));
}

export function useArticles() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPublishedArticles = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("articles")
        .select("id, title, slug, excerpt, tags, meta_title, meta_description, views_count, reading_time, published_at, author_id, created_at")
        .eq("status", "published")
        .is("hidden_at", null)
        .order("published_at", { ascending: false });

      if (error) {
        console.error("Failed to fetch articles:", error);
        setArticles([]);
        return;
      }

      const authorIds = [...new Set((data || []).map((a) => a.author_id))];
      let authorsMap = {};
      if (authorIds.length > 0) {
        const { data: profiles } = await supabase
          .from("profiles")
          .select("id, name, avatar_url, title, specializations")
          .in("id", authorIds);
        if (profiles) {
          profiles.forEach((p) => { authorsMap[p.id] = p; });
        }
      }

      const mapped = (data || []).map((a) => ({
        ...a,
        author: authorsMap[a.author_id] || { name: "Эксперт", avatar_url: "", title: "", specializations: [] }
      }));

      setArticles(mapped);
    } catch (err) {
      console.error("Error fetching articles:", err);
      setArticles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPublishedArticles();
  }, [fetchPublishedArticles]);

  return { articles, loading, refetch: fetchPublishedArticles };
}

export function useArticle(slug) {
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) { setLoading(false); return; }
    let mounted = true;

    const load = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from("articles")
          .select("*")
          .eq("slug", slug)
          .eq("status", "published")
          .is("hidden_at", null)
          .single();

        if (error || !data) {
          if (mounted) { setArticle(null); setLoading(false); }
          return;
        }

        const { data: authorProfile } = await supabase
          .from("profiles")
          .select("id, name, avatar_url, title, specializations")
          .eq("id", data.author_id)
          .single();

        supabase
          .from("articles")
          .update({ views_count: (data.views_count || 0) + 1 })
          .eq("id", data.id)
          .then(() => {});

        if (mounted) {
          setArticle({
            ...data,
            author: authorProfile || { name: "Эксперт", avatar_url: "", title: "", specializations: [] }
          });
        }
      } catch (err) {
        console.error("Error fetching article:", err);
        if (mounted) setArticle(null);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => { mounted = false; };
  }, [slug]);

  return { article, loading };
}

export function useExpertArticles(expertId) {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchArticles = useCallback(async () => {
    if (!expertId) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("articles")
        .select("*")
        .eq("author_id", expertId)
        .order("created_at", { ascending: false });

      if (!error && data) {
        setArticles(data);
      }
    } catch (err) {
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  }, [expertId]);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  const saveArticle = async (articleData) => {
    const slug = articleData.slug || await generateSlug(articleData.title);
    const readingTime = estimateReadingTime(articleData.content || "");
    const metaTitle = articleData.meta_title || articleData.title;
    const metaDesc = articleData.meta_description || articleData.excerpt || articleData.title;

    const payload = {
      author_id: expertId,
      title: articleData.title,
      slug,
      excerpt: articleData.excerpt || "",
      content: articleData.content || "",
      tags: articleData.tags || [],
      meta_title: metaTitle.slice(0, 70),
      meta_description: metaDesc.slice(0, 160),
      meta_keywords: (articleData.tags || []).join(", "),
      status: articleData.status || "draft",
      reading_time: readingTime,
      published_at: articleData.status === "published" ? new Date().toISOString() : null,
      updated_at: new Date().toISOString()
    };

    if (articleData.id) {
      const { data, error } = await supabase
        .from("articles")
        .update(payload)
        .eq("id", articleData.id)
        .select()
        .single();
      if (error) { console.error("Update error:", error); return null; }
      await fetchArticles();
      return data || true;
    } else {
      const { data, error } = await supabase
        .from("articles")
        .insert([payload])
        .select()
        .single();
      if (error) { console.error("Insert error:", error); return null; }
      await fetchArticles();
      return data || true;
    }
  };

  const deleteArticle = async (articleId) => {
    const { error } = await supabase
      .from("articles")
      .delete()
      .eq("id", articleId);
    if (error) { console.error("Delete error:", error); return false; }
    await fetchArticles();
    return true;
  };

  const toggleHideArticle = async (articleId, isCurrentlyHidden) => {
    const { error } = await supabase
      .from("articles")
      .update({
        hidden_at: isCurrentlyHidden ? null : new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq("id", articleId);
    if (error) { console.error("Toggle hide error:", error); return false; }
    await fetchArticles();
    return true;
  };

  return { articles, loading, saveArticle, deleteArticle, toggleHideArticle, refetch: fetchArticles };
}

export function useAutoSaveDraft(expertId) {
  const timerRef = useRef(null);
  const lastSavedRef = useRef("");
  const draftIdRef = useRef(null);
  const draftSlugRef = useRef(null);
  const [autoSaving, setAutoSaving] = useState(false);
  const [lastAutoSaved, setLastAutoSaved] = useState(null);

  const autoSave = useCallback(async (articleData) => {
    if (!expertId || !articleData.title?.trim()) return null;

    const fingerprint = JSON.stringify({
      title: articleData.title,
      excerpt: articleData.excerpt,
      content: articleData.content,
      tags: articleData.tags,
      meta_title: articleData.meta_title,
      meta_description: articleData.meta_description
    });

    if (fingerprint === lastSavedRef.current) return null;

    setAutoSaving(true);
    try {
      const readingTime = estimateReadingTime(articleData.content || "");
      const metaTitle = articleData.meta_title || articleData.title;
      const metaDesc = articleData.meta_description || articleData.excerpt || articleData.title;

      const payload = {
        author_id: expertId,
        title: articleData.title,
        excerpt: articleData.excerpt || "",
        content: articleData.content || "",
        tags: articleData.tags || [],
        meta_title: metaTitle.slice(0, 70),
        meta_description: metaDesc.slice(0, 160),
        meta_keywords: (articleData.tags || []).join(", "),
        status: "draft",
        reading_time: readingTime,
        updated_at: new Date().toISOString()
      };

      let result = null;

      const existingId = articleData.id || draftIdRef.current;

      if (existingId) {
        const { data, error } = await supabase
          .from("articles")
          .update(payload)
          .eq("id", existingId)
          .select()
          .single();
        if (!error) result = data;
      } else {
        const slug = await generateSlug(articleData.title);
        payload.slug = slug;
        const { data, error } = await supabase
          .from("articles")
          .insert([payload])
          .select()
          .single();
        if (!error) result = data;
      }

      if (result) {
        lastSavedRef.current = fingerprint;
        setLastAutoSaved(new Date());
        if (result.id && !draftIdRef.current) {
          draftIdRef.current = result.id;
        }
        if (result.slug && !draftSlugRef.current) {
          draftSlugRef.current = result.slug;
        }
      }
      return result;
    } catch (err) {
      console.error("Autosave error:", err);
      return null;
    } finally {
      setAutoSaving(false);
    }
  }, [expertId]);

  const scheduleAutoSave = useCallback((articleData) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      autoSave(articleData);
    }, 3000);
  }, [autoSave]);

  const cancelAutoSave = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const resetFingerprint = useCallback(() => {
    lastSavedRef.current = "";
    draftIdRef.current = null;
    draftSlugRef.current = null;
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return { scheduleAutoSave, cancelAutoSave, autoSaving, lastAutoSaved, resetFingerprint, draftIdRef, draftSlugRef };
}
